import { Button } from '@heroui/react';
import React, { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ActionBlock, ContentLoadError, PageTitle, PageWrapper, Spinner } from '../../../components';
import { useHeroToast } from '../../../hooks';
import { AdRequest, useAssignAdToHotels, useGetAd, usePatchAd } from '../../../queries';
import { AdForm, AdStats, HotelPicker } from '../components';

const FORM_ID = 'form-edit-ad';

export const AdEdit: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useHeroToast();

  const { data: ad, isLoading, error } = useGetAd(id);
  const { mutate: patch, isPending: isSaving } = usePatchAd();
  const { mutate: assign, isPending: isAssigning } = useAssignAdToHotels();

  // Bez `assigned_hotels` — tym zarządza blok niżej, osobnym zapisem.
  const defaultValues = useMemo(() => (ad ? {
    name: ad.name,
    image: ad.image,
    type: ad.type,
    order: ad.order,
    url: ad.url,
  } : undefined), [ad]);

  const handleSubmit = (data: AdRequest): void => {
    if (!id) return;
    patch(
      { adId: id, data },
      {
        onSuccess: (result) => {
          const assigned = result.assigned_hotels?.length ?? 0;
          showSuccess(`Zapisano — zmiana jest widoczna w ${assigned} hotelach`);
          navigate('/ads');
        },
        onError: (mutationError) => showError(
          mutationError.message || 'Nie udało się zapisać reklamy',
        ),
      },
    );
  };

  const handleAssign = (hotelIds: string[]): void => {
    if (!id) return;
    assign(
      { adId: id, hotelIds },
      {
        onSuccess: (result) => {
          const added = result.added?.length ?? 0;
          const removed = result.removed?.length ?? 0;
          const skipped = result.skipped ?? [];
          // Backend odsiewa id bez dokumentu w `hotels`. Milczenie o tym sprawia,
          // że nieudane przypisanie wygląda jak udane — stąd osobny komunikat.
          if (skipped.length > 0) {
            showError(`Pominięto ${skipped.length} hoteli — brak dokumentu w bazie: ${skipped.join(', ')}`);
            return;
          }
          showSuccess(`Przypisanie zapisane — dodano: ${added}, zdjęto: ${removed}`);
        },
        onError: (mutationError) => showError(
          mutationError.message || 'Nie udało się zmienić przypisania',
        ),
      },
    );
  };

  if (error) {
    return (
      <PageWrapper>
        <ContentLoadError message={error.message} />
      </PageWrapper>
    );
  }

  if (isLoading) {
    return (
      <PageWrapper>
        <div className="py-10 flex justify-center">
          <Spinner showLabel={false} />
        </div>
      </PageWrapper>
    );
  }

  if (!ad) {
    return (
      <PageWrapper>
        <ContentLoadError message="Nie znaleziono takiej reklamy." />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageTitle
        title={`Reklama: ${ad.name || ad.id}`}
        actions={(
          <Button
            size="sm"
            color="secondary"
            radius="sm"
            type="submit"
            form={FORM_ID}
            isLoading={isSaving}
          >
            Zapisz
          </Button>
        )}
      />

      <AdForm onSubmit={handleSubmit} formDataId={FORM_ID} defaultValues={defaultValues} />

      {/* Przypisania zapisują się od razu, w oderwaniu od formularza — inaczej
          dopięcie hotelu wymagałoby przejścia przez walidację całej kreacji. */}
      <ActionBlock
        title="Przypisane hotele"
        description="Zmiana zapisuje się natychmiast: dopięty hotel dostaje reklamę w swojej kolekcji `ads`, odpięty traci ją z niej."
      >
        <HotelPicker
          value={ad.assigned_hotels}
          onChange={handleAssign}
          isSaving={isAssigning}
        />
      </ActionBlock>

      <AdStats adId={ad.id} assignedHotels={ad.assigned_hotels} />
    </PageWrapper>
  );
};
